import toast from "react-hot-toast";
import { AxiosError } from "axios";
import { compareAsc } from "date-fns";
import { IMetricModelOutput, IMetricModelOutputsGetQueryParameters, Route } from "@edgetier/watchtower-types";
import { useQuery, UseQueryOptions } from "react-query";

import { getServerError } from "utilities/get-server-error";
import { getAllPaginatedItems } from "utilities/get-all-paginated-items";

/**
 * Get and sort the output of a metric model.
 * @param url             Metric model output URL.
 * @param queryParameters Optional parameters to pass to the route.
 * @returns               Array of metric model outputs.
 */
const getMetricModelOutputs = async (url: string, queryParameters: IMetricModelOutputsGetQueryParameters) => {
    const params = queryParameters;
    const metricModelOutputs = await getAllPaginatedItems<IMetricModelOutput>(url, { params });
    return metricModelOutputs.slice().sort((one, two) => compareAsc(one.dateTime, two.dateTime));
};

/**
 * A reusable query for requesting the outputs of a metric model.
 * @param modelId         The model ID of the outputs being requested.
 * @param queryParameters Optional parameters to pass to the route.
 * @param configuration   Optional configuration object for the query.
 */
const useMetricModelOutputs = (
    modelId: string,
    queryParameters: IMetricModelOutputsGetQueryParameters,
    configuration: UseQueryOptions<IMetricModelOutput[], AxiosError> = {}
) => {
    const url = Route.MetricModelOutputs.replace(":modelId", modelId);
    return useQuery<IMetricModelOutput[], AxiosError>(
        [url, queryParameters],
        () => getMetricModelOutputs(url, queryParameters),
        {
            onError: (serverError: AxiosError<any>) => {
                toast.error(getServerError(serverError));
            },
            ...configuration,
        }
    );
};

export default useMetricModelOutputs;
